// whitelabelService.js
// Serviço separado para funcionalidades de whitelabel que não interferem no tema principal

// Cache para os dados dos tenants
const tenantCache = new Map();

// Dados mockados dos tenants
const mockTenants = {
  'fullcontroller': {
    id: 'fullcontroller',
    name: 'FullController',
    colors: {
      primary: "#312B59",
      gradient1: "#312B59",
      gradient2: "#6944EC",
      secondary: "#6944EC"
    },
    logo: "https://data4company.blob.core.windows.net/customizations/logos/fullcontroller (2).png",
    fav_icon: "https://data4company.blob.core.windows.net/customizations/logos/fullcontroller (2).png",
    backgroundImage: null, // Para página de login
    customStyles: {
      // Estilos adicionais específicos do tenant
      sidebarBackground: "#312B59",
      headerBackground: "#6944EC",
      buttonPrimary: "#312B59",
      buttonSecondary: "#6944EC"
    }
  },
  'cope': {
    id: 'cope',
    name: 'Cope',
    colors: {
      primary: "#2596be",
      gradient1: "#2596be",
      gradient2: "#010704",
      secondary: "#010704"
    },
    logo: "https://data4company.blob.core.windows.net/customizations/logos/cope_logo.png",
    fav_icon: "https://data4company.blob.core.windows.net/customizations/logos/cope_logo.png",
    backgroundImage: null,
    customStyles: {
      sidebarBackground: "#2596be",
      headerBackground: "#010704",
      buttonPrimary: "#2596be",
      buttonSecondary: "#010704"
    }
  },
  'green': {
    id: 'green',
    name: 'Green Company',
    colors: {
      primary: "#22c55e",
      gradient1: "#22c55e",
      gradient2: "#16a34a",
      secondary: "#16a34a"
    },
    logo: "/favicon.ico",
    fav_icon: "/favicon.ico",
    backgroundImage: null,
    customStyles: {
      sidebarBackground: "#22c55e",
      headerBackground: "#16a34a",
      buttonPrimary: "#22c55e",
      buttonSecondary: "#16a34a"
    }
  },
  'orange': {
    id: 'orange',
    name: 'Orange Corp',
    colors: {
      primary: "#f97316",
      gradient1: "#f97316",
      gradient2: "#ea580c",
      secondary: "#ea580c"
    },
    logo: "/favicon.ico",
    fav_icon: "/favicon.ico",
    backgroundImage: null,
    customStyles: {
      sidebarBackground: "#f97316",
      headerBackground: "#ea580c",
      buttonPrimary: "#f97316",
      buttonSecondary: "#ea580c"
    }
  }
};

/**
 * Busca dados do tenant
 * @param {string} tenantId - ID do tenant
 * @returns {Promise<Object>} - Dados do tenant
 */
export const fetchTenantData = async (tenantId) => {
  try {
    // Verificar cache primeiro
    if (tenantCache.has(tenantId)) {
      return tenantCache.get(tenantId);
    }

    // Em produção, fazer chamada para API
    if (process.env.NODE_ENV === 'production') {
      // Aqui você faria a chamada para sua API
      // const response = await api.get(`/tenants/${tenantId}`);
      // const tenantData = response.data;
      
      // Por enquanto, usar mock
      const tenantData = mockTenants[tenantId];
      
      if (tenantData) {
        tenantCache.set(tenantId, tenantData);
        return tenantData;
      }
    }

    // Em desenvolvimento, usar dados mockados
    const mockData = mockTenants[tenantId];
    if (mockData) {
      tenantCache.set(tenantId, mockData);
      return mockData;
    }

    throw new Error(`Tenant ${tenantId} não encontrado`);
  } catch (error) {
    console.error('Erro ao buscar dados do tenant:', error);
    return null;
  }
};

/**
 * Obtém o ID do tenant da URL ou variável de ambiente
 * @returns {string} - ID do tenant
 */
export const getTenantId = () => {
  // Verificar se há tenant na URL (ex: /tenant/cope)
  const pathSegments = window.location.pathname.split('/');
  const tenantIndex = pathSegments.findIndex(segment => segment === 'tenant');
  
  if (tenantIndex !== -1 && pathSegments[tenantIndex + 1]) {
    return pathSegments[tenantIndex + 1];
  }

  // Verificar variável de ambiente
  return process.env.REACT_APP_TENANT_ID || 'fullcontroller';
};

/**
 * Atualiza o favicon da página
 * @param {string} faviconUrl - URL do favicon
 */
export const updateFavicon = (faviconUrl) => {
  if (!faviconUrl) return;

  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = faviconUrl;
  
  // Remover link antigo se existir
  const oldLink = document.querySelector("link[rel*='icon']");
  if (oldLink) {
    oldLink.remove();
  }
  
  document.getElementsByTagName('head')[0].appendChild(link);
};

/**
 * Atualiza o título da página
 * @param {string} tenantName - Nome do tenant
 */
export const updatePageTitle = (tenantName) => {
  if (tenantName) {
    document.title = `${tenantName}`;
  }
};

/**
 * Aplica estilos customizados do tenant (opcional)
 * @param {Object} tenantData - Dados do tenant
 */
export const applyTenantStyles = (tenantData) => {
  if (!tenantData?.customStyles) return;

  // Aqui você pode aplicar estilos CSS customizados
  // Por exemplo, variáveis CSS ou estilos inline
  const root = document.documentElement;
  
  if (tenantData.customStyles.sidebarBackground) {
    root.style.setProperty('--tenant-sidebar-bg', tenantData.customStyles.sidebarBackground);
  }
  
  if (tenantData.customStyles.headerBackground) {
    root.style.setProperty('--tenant-header-bg', tenantData.customStyles.headerBackground);
  }
};

/**
 * Limpa o cache de tenants
 */
export const clearTenantCache = () => {
  tenantCache.clear();
}; 